import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { animals } from '@/router/animals'
import shopsRoutes from '@/router/shops'
import { farmRoutes } from '@/router/farmRoutes'
import { workerRoutes } from '@/router/workers'
import { healthRoutes } from '@/router/healthRoutes'
import HomeView from '@/views/HomeView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import RegisterView from '@/views/auth/RegisterView.vue'
import AdminDashboard from '@/views/dashboard/AdminDashboard.vue'
import StaffView from '@/views/supervisor/StaffView.vue'
import AboutView from '@/views/AboutView.vue'
import ContactView from '@/views/ContactView.vue'
import UsersView from '@/views/dashboard/UsersView.vue'
import SupervisorDashboard from '@/views/dashboard/SupervisorDashboard.vue'
import FuelView from '@/views/fuel/FuelView.vue'
import FuelCreate from '@/views/fuel/FuelCreate.vue'
import FuelAnalytics from '@/views/fuel/FuelAnalytics.vue'
import EquipmentView from '@/views/equipment/EquipmentView.vue'
import EquipmentCreate from '@/views/equipment/EquipmentCreate.vue'
import EquipmentDetail from '@/views/equipment/EquipmentDetail.vue'
import UsageView from '@/views/usage/UsageView.vue'
import UsageCreate from '@/views/usage/UsageCreate.vue'
import UsageReports from '@/views/usage/UsageReports.vue'
import MaintenanceView from '@/views/maintenance/MaintenanceView.vue'
import MaintenanceCreate from '@/views/maintenance/MaintenanceCreate.vue'
import MaintenanceDetail from '@/views/maintenance/MaintenanceDetail.vue'
import ProfileView from '@/views/profile/ProfileView.vue'


interface RouteMeta {
  requiresAuth: boolean
  allowedRoles?: string[]
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    meta: { requiresAuth: false },
  },
  {
    path: '/about',
    name: 'About',
    component: AboutView,
    meta: { requiresAuth: false },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: ContactView,
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false },
  },
  {
    path: '/register',
    name: 'Register',
    redirect: '/login'
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password/:uid/:token',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, allowedRoles: ['ADMIN'] },
  },
  {
    path: '/admin/users',
    name: 'UsersView',
    component: UsersView,
    meta: { requiresAuth: true, allowedRoles: ['ADMIN'] },
  },
  {
    path: '/admin/reports',
    name: 'ComprehensiveReports',
    component: () => import('@/views/admin/ComprehensiveReportsView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['ADMIN'] },
  },
  {
    path: '/supervisor',
    name: 'SupervisorDashboard',
    component: SupervisorDashboard,
    meta: { requiresAuth: true, allowedRoles: ['SUPERVISOR'] },
  },
  {
    path: '/customer',
    name: 'CustomerDashboard',
    component: () => import('@/views/dashboard/CustomerDashboard.vue'),
    meta: { requiresAuth: true, allowedRoles: ['CUSTOMER'] },
  },
  {
    path: '/fuel',
    name: 'FuelView',
    component: FuelView,
    meta: { requiresAuth: true },
  },
  {
    path: '/fuel/create',
    name: 'FuelCreate',
    component: FuelCreate,
    meta: { requiresAuth: true },
  },
  {
    path: '/fuel/analytics',
    name: 'FuelAnalytics',
    component: FuelAnalytics,
    meta: { requiresAuth: true },
  },
  {
    path: '/equipment',
    name: 'EquipmentView',
    component: EquipmentView,
    meta: { requiresAuth: true },
  },
  {
    path: '/equipment/create',
    name: 'EquipmentCreate',
    component: EquipmentCreate,
    meta: { requiresAuth: true },
  },
  {
    path: '/equipment/:id',
    name: 'EquipmentDetail',
    component: EquipmentDetail,
    meta: { requiresAuth: true },
  },
  {
    path: '/usage',
    name: 'UsageView',
    component: UsageView,
    meta: { requiresAuth: true },
  },
  {
    path: '/usage/create',
    name: 'UsageCreate',
    component: UsageCreate,
    meta: { requiresAuth: true },
  },
  {
    path: '/usage/reports',
    name: 'UsageReports',
    component: UsageReports,
    meta: { requiresAuth: true },
  },
  {
    path: '/maintenance',
    name: 'MaintenanceView',
    component: MaintenanceView,
    meta: { requiresAuth: true },
  },
  {
    path: '/maintenance/create',
    name: 'MaintenanceCreate',
    component: MaintenanceCreate,
    meta: { requiresAuth: true },
  },
  {
    path: '/maintenance/:id',
    name: 'MaintenanceDetail',
    component: MaintenanceDetail,
    meta: { requiresAuth: true },
  },
  {
    path: '/breeding',
    name: 'BreedingView',
    component: () => import('@/views/breeding/BreedingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/feeding',
    name: 'FeedingView',
    component: () => import('@/views/feeding/FeedingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/production',
    name: 'ProductionDashboard',
    component: () => import('@/views/production/ProductionDashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/finance',
    name: 'FinanceDashboard',
    component: () => import('@/views/finance/FinanceDashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/finance/general-expenses',
    name: 'GeneralExpenses',
    component: () => import('@/views/finance/GeneralExpenses.vue'),
    meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'SUPERVISOR'] },
  },
  ...shopsRoutes,
  ...animals,
  ...animals,
  ...farmRoutes,
  ...healthRoutes,
  {
    path: '/admin/messages',
    name: 'Messages',
    component: () => import('@/views/admin/MessagesView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'SUPERVISOR'] },
  },
  {
    path: '/supervisor/users',
    name: 'Staff',
    component: StaffView,
    meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'SUPERVISOR'] },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfileView,
    meta: { requiresAuth: true },
  },
  ...workerRoutes,
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const meta = to.meta as RouteMeta

  if (!authStore.isAuthenticated && localStorage.getItem('accessToken')) {
    await authStore.checkAuth()
  }

  const userRole = authStore.user?.role

  if (meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
  } else if (meta.allowedRoles && (!userRole || !meta.allowedRoles.includes(userRole))) {
    next('/')
  } else {
    next()
  }
})

export default router
